import React, {Component, useMemo, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {
    AllCommunityModule,
    ClientSideRowModelModule,
    ColumnMenuModule,
    ColumnsToolPanelModule,
    ContextMenuModule,
    CsvExportModule,
    ExcelExportModule,
    SetFilterModule,
    MultiFilterModule,
} from "ag-grid-enterprise";
import {Helmet} from "react-helmet";
import NotificationManager from "react-notifications/lib/NotificationManager";
import {
    getColumnModeInSession,
    getDefaultAgGridSidebarProps,
    getUseColumnStateInSession,
    getUseFilterStateInSession,
    onColumnStateChangedHelper,
    onFilterChangedHelper,
    onGridReadyHelper,
    onGridReadyHelperForColumnState,
    updateColumnModeInSessionHelper,
    updateUseColumnStateHelper,
    updateUseFilterStateHelper,
} from "../../utils/gridFilterStateAndColumnStateHelper";
import {ClearRefresh} from "../../components/clearRefreshButtons";
import privatePageHeaderHelper from "../../utils/privatePageHeaderHelper";
import {
    ClickToShowColumnOptionsWithToggleButtonGroup,
    customColumnModeText,
    minColumnModeText,
    standardApplyMinimumOrMediumColumnMode,
} from "../../components/clickToShowButtons";
import {DisplayOptions} from "../../components/displayOptionsComponent";
import DTPicker, {dateFilterParametersInHeaderClientSideGrid} from "../../utils/DTPicker";
import CustomNameCellEditor from "../../utils/customCellEditor";
import {
    updatePurgeHistoryGridUseFilterStateReactive,
    updatePurgeHistoryGridFilterModelReactive,
    updatePurgeHistoryGridUseColumnStateReactive,
    updatePurgeHistoryGridColumnStateReactive,
    updatePurgeHistoryGridColumnModeReactive,
    purgeGroup
} from "../api/purgeHistoryApi";
import {listAllGroupsCCOnly} from "../api/ccOnlyAddToGroupApi";
import {
    loadDataWithSSEAndStartChangeStreamListener,
    standardHandleInsertEvent,
    standardHandlePopulateGrid,
    standardHandleUpdateAndReplaceEvent,
} from "../../utils/sseAndChangeStreamHelper";
import {standardExcelExportHelper, standardExcelExportObjectInContextMenu} from "../../utils/excelExportHelper";
import Modal from "react-modal";
import {Button, ThemeProvider} from "@mui/material";
import {buttonTheme} from "../../utils/muiStyling";
import {LoadingButton} from "@mui/lab";
import {MuiAutocompleteForZenGroupsWithoutCreateGroupOption, MuiCloseIconButton, MuiIconButtonWithTooltip, MuiIconButtonWithTooltipAndBox} from "../../components/muiComponents";
import {ConfirmationModal} from "../../components/confirmationModal";
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import {dateValueFormatter} from "../../utils/gridDateFormatter";
import {defaultClientSideTextFilterParams} from "../../utils/filterHelper";

const gridColumnStateSessionVariableName = "purgeHistoryGridColumnState"
const gridFilterStateSessionVariableName = "purgeHistoryGridFilterState"

const minColumnIds = ["zenGroupDisplayName", "requestedByUsername", "status", "dateRequested"]

const PURGE_STATUS_LABEL = {
    IN_PROGRESS: "In Progress",
    COMPLETE:    "Complete",
    FAILED:      "Failed",
}

const PURGE_STATUS_COLOR = {
    IN_PROGRESS: "#f59e0b",
    COMPLETE:    "#16a34a",
    FAILED:      "#dc2626",
}

function purgeStatusCellRenderer(params) {
    const raw   = params.value
    const label = PURGE_STATUS_LABEL[raw] ?? raw ?? ""
    const color = PURGE_STATUS_COLOR[raw] ?? "#6b7280"
    return (
        <span style={{display: "inline-flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "0.8rem", color}}>
            {label}
        </span>
    )
}

// ── Meta Counts cell — shows a summary button that opens a detail modal ───────
function MetaCountsCellRenderer(params) {
    const rawCounts = params.value
    const [modalOpen, setModalOpen] = useState(false)

    if (!rawCounts || rawCounts.length === 0) {
        return <span style={{color: "#94a3b8", fontSize: "0.75rem"}}>—</span>
    }

    // Sort alphabetically by collection name for the modal display
    const counts = [...rawCounts].sort((a, b) => a.collection.localeCompare(b.collection))

    const total   = counts.reduce((s, mc) => s + mc.initialCount, 0)
    const cleared = counts.filter(mc => mc.remaining === 0).length

    return (
        <>
            <div style={{display: "flex", alignItems: "center", height: "100%", gap: "4px"}}>
                <MuiIconButtonWithTooltip
                    icon={<FindInPageIcon />}
                    tooltipTitle={"View collection counts"}
                    tooltipPlacement={"bottom-start"}
                    onClick={() => setModalOpen(true)}
                />
                <span>{cleared}/{counts.length} cleared · {total.toLocaleString()} total</span>
            </div>

            <Modal
                isOpen={modalOpen}
                onRequestClose={() => setModalOpen(false)}
                shouldCloseOnOverlayClick={true}
                contentLabel="Collection Counts"
                className="focus:outline-none focus:shadow-sm border-0 flex relative z-50 bg-white max-w-3xl inset-y-10 mx-auto rounded-2xl shadow-2xl"
                overlayClassName="z-50 bg-black bg-opacity-30 fixed inset-0 overflow-scroll flex items-start justify-center"
            >
                <div style={{display: "flex", flexDirection: "column", width: "100%", padding: "28px 32px", gap: "16px"}}>
                    {/* Header */}
                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                        <div>
                            <h2 style={{margin: 0, fontSize: "1.25rem", fontWeight: 700, color: "#0f172a"}}>
                                Collection Counts
                            </h2>
                            <p style={{margin: "4px 0 0", fontSize: "0.8rem", color: "#64748b"}}>
                                {cleared} of {counts.length} collections cleared &nbsp;·&nbsp; {total.toLocaleString()} total docs
                            </p>
                        </div>
                        <MuiCloseIconButton onClick={() => setModalOpen(false)} />
                    </div>

                    <hr style={{margin: "0", borderColor: "#e2e8f0"}} />

                    {/* Table */}
                    <div style={{overflowY: "auto"}}>
                        <table style={{width: "100%", borderCollapse: "collapse", fontSize: "0.82rem"}}>
                            <thead>
                            <tr style={{borderBottom: "2px solid #e2e8f0"}}>
                                <th style={{textAlign: "left",  padding: "6px 8px", color: "#64748b", fontWeight: 600}}>Collection</th>
                                <th style={{textAlign: "right", padding: "6px 8px", color: "#64748b", fontWeight: 600}}>Initial</th>
                                <th style={{textAlign: "right", padding: "6px 8px", color: "#64748b", fontWeight: 600}}>Remaining</th>
                                <th style={{textAlign: "right", padding: "6px 8px", color: "#64748b", fontWeight: 600}}>Status</th>
                            </tr>
                            </thead>
                            <tbody>
                            {counts.map((mc, i) => {
                                const zeroed  = mc.remaining === 0
                                const pct     = mc.initialCount === 0 ? 100 : Math.round(((mc.initialCount - mc.remaining) / mc.initialCount) * 100)
                                return (
                                    <tr
                                        key={i}
                                        style={{
                                            borderBottom: "1px solid #f1f5f9",
                                            background:   i % 2 === 0 ? "#fff" : "#fafafa",
                                        }}
                                    >
                                        <td style={{padding: "8px 8px", color: "#1e293b", fontWeight: 500, fontFamily: "monospace"}}>
                                            {mc.collection}
                                        </td>
                                        <td style={{padding: "8px 8px", textAlign: "right", color: "#475569"}}>
                                            {mc.initialCount.toLocaleString()}
                                        </td>
                                        <td style={{padding: "8px 8px", textAlign: "right", color: zeroed ? "#15803d" : "#b45309", fontWeight: 600}}>
                                            {mc.remaining.toLocaleString()}
                                        </td>
                                        <td style={{padding: "8px 8px", textAlign: "right"}}>
                                            <div style={{display: "inline-flex", alignItems: "center", gap: "6px"}}>
                                                <div style={{
                                                    width: "60px", height: "6px", borderRadius: "9999px",
                                                    background: "#e2e8f0", overflow: "hidden",
                                                }}>
                                                    <div style={{
                                                        width:        `${pct}%`,
                                                        height:       "100%",
                                                        borderRadius: "9999px",
                                                        background:   zeroed ? "#16a34a" : "#f59e0b",
                                                        transition:   "width 0.3s ease",
                                                    }}/>
                                                </div>
                                                <span style={{fontSize: "0.7rem", color: "#94a3b8", minWidth: "28px", textAlign: "right"}}>
                                                        {pct}%
                                                    </span>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>
        </>
    )
}

export default function PurgeHistory() {
    const [isLoading, setIsLoading] = useState(false)
    const [gridApi,   setGridApi]   = useState(null)

    // ── Purge modal state ─────────────────────────────────────────────────────
    const [purgeGroupModalOpen,        setPurgeGroupModalOpen]        = useState(false)
    const [groupToPurge,               setGroupToPurge]               = useState(null)
    const [allGroupsList,              setAllGroupsList]              = useState([])
    const [showPurgeGroupConfirmation, setShowPurgeGroupConfirmation] = useState(false)

    // ── Grid state ────────────────────────────────────────────────────────────
    const [useFilterState, setUseFilterState] = useState(getUseFilterStateInSession(gridFilterStateSessionVariableName))
    const [useColumnState, setUseColumnState] = useState(getUseColumnStateInSession(gridColumnStateSessionVariableName))
    const [columnMode,     setColumnMode]     = useState(getColumnModeInSession(gridColumnStateSessionVariableName))

    const [sseDataPullActive,          setSSEDataPullActive]          = useState(true)
    const [asyncTransactionWaitMillis, setAsyncTransactionWaitMillis] = useState(200)

    function toggleUpdateUseFilterState(toggleSetting) {
        updateUseFilterStateHelper(toggleSetting, gridFilterStateSessionVariableName, updatePurgeHistoryGridUseFilterStateReactive)
    }
    function toggleUpdateUseColumnState(toggleSetting) {
        updateUseColumnStateHelper(toggleSetting, gridColumnStateSessionVariableName, updatePurgeHistoryGridUseColumnStateReactive)
    }
    function excelExport() {
        standardExcelExportHelper(gridApi, sseDataPullActive, "purgeHistoryGridExport")
    }
    async function refreshGrid() {}

    // ── Purge modal handlers ──────────────────────────────────────────────────
    function openPurgeModal() {
        setPurgeGroupModalOpen(true)
        if (allGroupsList.length === 0) {
            listAllGroupsCCOnly().then(response => {
                const formatted = response
                    .map(group => ({
                        value: group.id,
                        label: group.userSetFriendlyName || group.friendlyName || group.id,
                    }))
                    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
                setAllGroupsList(formatted)
            }).catch(() => {})
        }
    }
    function resetPurgeGroupModal() {
        setPurgeGroupModalOpen(false)
        setGroupToPurge(null)
        setShowPurgeGroupConfirmation(false)
    }
    const handleSubmitPurgeGroup = (e) => {
        e.preventDefault()
        if (!groupToPurge) {
            NotificationManager.error("Select a group")
        } else {
            setShowPurgeGroupConfirmation(true)
        }
    }
    const handlePurgeGroup = () => {
        setIsLoading(true)
        purgeGroup(groupToPurge).then(() => {
            resetPurgeGroupModal()
            NotificationManager.success("Successfully Purged Group")
            setIsLoading(false)
        }).catch(error => {
            NotificationManager.error(error.message || "Unexpected Error Purging Group.")
            setIsLoading(false)
        })
    }

    // ── Column defs ───────────────────────────────────────────────────────────
    const [columnDefs] = useState([
        {
            field:        "zenGroupDisplayName",
            headerName:   "Group",
            initialWidth: 300,
            sortable:     true,
            filter:       "agSetColumnFilter",
            filterParams: {
                buttons:           ["reset", "apply", "cancel"],
                refreshValuesOnOpen: true,
                suppressSorting:   false,
            },
        },
        {
            field:        "requestedByUsername",
            headerName:   "Requested By",
            initialWidth: 280,
            sortable:     true,
            filter:       "agTextColumnFilter",
            filterParams: defaultClientSideTextFilterParams,
        },
        {
            field:        "status",
            headerName:   "Status",
            initialWidth: 160,
            sortable:     true,
            filter:       "agSetColumnFilter",
            filterParams: {
                buttons:        ["reset", "apply", "cancel"],
                values:         Object.keys(PURGE_STATUS_LABEL),
                valueFormatter: (p) => PURGE_STATUS_LABEL[p.value] ?? p.value,
            },
            valueFormatter: (p) => PURGE_STATUS_LABEL[p.value] ?? p.value,
            cellRenderer:   purgeStatusCellRenderer,
        },
        {
            field:        "dateRequested",
            headerName:   "Date Requested",
            initialWidth: 330,
            sortable:     true,
            filter:       "agDateColumnFilter",
            filterParams: dateFilterParametersInHeaderClientSideGrid(),
            valueFormatter: dateValueFormatter
        },
        {
            field:        "dateCompleted",
            headerName:   "Date Completed",
            initialWidth: 330,
            sortable:     true,
            filter:       "agDateColumnFilter",
            filterParams: dateFilterParametersInHeaderClientSideGrid(),
            valueFormatter: dateValueFormatter
        },
        {
            field:        "metaCounts",
            headerName:   "Collection Counts",
            initialWidth: 250,
            sortable:     false,
            filter:       false,
            cellRenderer: MetaCountsCellRenderer,
            valueFormatter: (params) => {
                const counts = params.value
                if (!counts || counts.length === 0) return ""
                const total   = counts.reduce((s, mc) => s + Number(mc.initialCount), 0)
                const cleared = counts.filter(mc => mc.remaining === 0).length
                return `${cleared}/${counts.length} cleared · ${total} total`
            },
        },
    ])

    const [defaultColDef] = useState({
        resizable:             true,
        filterParams:          null,
        floatingFilter:        true,
        headerClass:           "border-0 border-b-0",
        cellClass:             "outline:none",
        enableCellChangeFlash: true,
        autoHeight:            false,
        cellDataType:          false,
    })

    const sideBar      = useMemo(() => getDefaultAgGridSidebarProps(325), [])
    const rowSelection = useMemo(() => ({
        mode:                 "singleRow",
        enableClickSelection: true,
        checkboxes:           false,
        headerCheckbox:       false,
    }), [])

    return (
        <div className="flex flex-col h-full">
            <Helmet>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title>Group Purge History</title>
                <script src="https://js.stripe.com/v3/" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap"
                    rel="stylesheet"
                />
            </Helmet>
            <div className="flex flex-1 flex-col flex-nowrap gap-y-3 h-full">
                {privatePageHeaderHelper("Group Purge History")}
                <hr className="h-0.5" />
                <DisplayOptions
                    useFilterStateSettingToggled={useFilterState}
                    setUseFilterStateSettingToggled={setUseFilterState}
                    toggleUpdateUseFilterState={toggleUpdateUseFilterState}
                    useColumnStateSettingToggled={useColumnState}
                    setUseColumnStateSettingToggled={setUseColumnState}
                    toggleUpdateUseColumnState={toggleUpdateUseColumnState}
                />
                <div className={"flex flex-row justify-between gap-x-1 gap-y-3"}>
                    <div className={"self-end flex flex-col gap-y-3"}>
                        <div className="flex flex-row justify-start gap-x-6 flex-wrap gap-y-2 items-center">
                            <MuiIconButtonWithTooltipAndBox
                                icon={<DeleteSweepIcon />}
                                tooltipTitle={"Purge a Group"}
                                tooltipPlacement={"top"}
                                onClick={openPurgeModal}
                            />
                        </div>
                    </div>
                    <div className={"flex flex-row flex-wrap gap-y-3 gap-x-8 self-end justify-end"}>
                        <ClickToShowColumnOptionsWithToggleButtonGroup
                            columnMode={columnMode}
                            setColumnMode={setColumnMode}
                            gridColumnStateSessionVariableName={gridColumnStateSessionVariableName}
                            gridApi={gridApi}
                            disableMediumOption={true}
                            minColumnIds={minColumnIds}
                            updateGridColumnModeFunction={updatePurgeHistoryGridColumnModeReactive}
                        />
                        <ClearRefresh
                            gridApi={gridApi}
                            showRefreshIcon={false}
                            refreshGridFunction={refreshGrid}
                            sseDataPullActive={sseDataPullActive}
                            showExcelExportIcon={true}
                            excelExportFunction={excelExport}
                        />
                    </div>
                </div>
                <div className="h-full flex flex-col gap-y-5" id="gridRoot">
                    {getGrid()}
                </div>
            </div>

            {/* ── Purge Group Modal ── */}
            <Modal
                contentLabel="Purge Group"
                isOpen={purgeGroupModalOpen}
                onRequestClose={resetPurgeGroupModal}
                shouldCloseOnOverlayClick={true}
                className={`focus:outline-none focus:shadow-sm border-2 flex relative z-50 bg-white max-w-2xl inset-y-10 mx-auto rounded-2xl`}
                overlayClassName="z-50 bg-black bg-opacity-5 fixed inset-0 overflow-scroll"
            >
                <div className="flex flex-1 flex-col p-8 w-full ml-4 mr-4 gap-y-3">
                    <div className="flex flex-row justify-between">
                        <h1 className="font-bold text-3xl">Purge a Group</h1>
                        <MuiCloseIconButton onClick={resetPurgeGroupModal} />
                    </div>
                    <hr className="h-0.5" />
                    <form className="flex flex-1 flex-col w-full" onSubmit={handleSubmitPurgeGroup}>
                        <p className="mt-2 mb-4 text-gray-700 font-medium">
                            This operation will permanently delete all incident data, browser data, notifications, roles,
                            and GridFS files associated with the selected group. All users will be removed from the group
                            (but not deleted from the system). The group itself and all agents will remain, though certain
                            client information fields (IP addresses, machine names, FQDN) will be unset from agents.
                        </p>
                        <label className="mb-1">Select Group to Purge</label>
                        <div className="flex flex-1 flex-col w-full gap-y-5">
                            <MuiAutocompleteForZenGroupsWithoutCreateGroupOption
                                disabled={false}
                                value={groupToPurge}
                                zenGroupDropdownOptionsList={allGroupsList}
                                onChange={(event, value) => {
                                    setGroupToPurge(value?.value)
                                }}
                            />
                            <ThemeProvider theme={buttonTheme}>
                                <LoadingButton variant={"contained"} color={"primary"} type={"submit"}>
                                    Purge Group
                                </LoadingButton>
                            </ThemeProvider>
                        </div>
                    </form>
                </div>
            </Modal>

            <ConfirmationModal
                text="You are about to purge this group, would you like to continue?"
                onConfirm={handlePurgeGroup}
                onClose={() => setShowPurgeGroupConfirmation(false)}
                opened={showPurgeGroupConfirmation}
            />
        </div>
    )

    function getGrid() {
        return (
            <Grid
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                sideBar={sideBar}
                rowSelection={rowSelection}
                setGridApi={setGridApi}
                columnMode={columnMode}
                setColumnMode={setColumnMode}
                gridColumnStateSessionVariableName={gridColumnStateSessionVariableName}
                gridFilterStateSessionVariableName={gridFilterStateSessionVariableName}
                minColumnIds={minColumnIds}
                updateColumnModeReactive={updatePurgeHistoryGridColumnModeReactive}
                updateFilterModelReactive={updatePurgeHistoryGridFilterModelReactive}
                updateColumnStateReactive={updatePurgeHistoryGridColumnStateReactive}
                sseDataPullActive={sseDataPullActive}
                setSSEDataPullActive={setSSEDataPullActive}
                asyncTransactionWaitMillis={asyncTransactionWaitMillis}
                setAsyncTransactionWaitMillis={setAsyncTransactionWaitMillis}
                excelExport={excelExport}
            />
        )
    }
}

class Grid extends Component {
    rowData = []
    updateTransactionsToApply = []
    abortController           = new AbortController()

    componentWillUnmount() {
        this.abortController.abort()
    }

    onColumnStateChanged = (params) => {
        const {gridColumnStateSessionVariableName, updateColumnModeReactive, updateColumnStateReactive, setColumnMode} = this.props
        if (params.source !== "api" && params.source !== "gridOptionsChanged") {
            setColumnMode && setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, updateColumnStateReactive)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, updateColumnModeReactive, customColumnModeText)
        } else if (params.source === "api" && params.type === "sortChanged") {
            setColumnMode && setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, updateColumnStateReactive)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, updateColumnModeReactive, customColumnModeText)
        }
    }

    getContextMenuItems = () => {
        const excelExport = this.props.excelExport
        return [
            standardExcelExportObjectInContextMenu(excelExport),
            "resetColumns",
            "autoSizeAll",
        ]
    }

    applyFilterStateAfterSSE(gridReadyParams) {
        /*
            Wait to apply saved grid filters until initial grid is done loading with data because
            asyncTransactionWaitMillis is set to 200ms initially so every 200ms rows will be added to the grid.
            We need to wait until rows are added so the group name set filter has all values populated by ag grid.
            onGridReadyHelper checks useFilterState internally before applying.
        */
        const timer = setTimeout(() => {
            onGridReadyHelper(gridReadyParams, gridFilterStateSessionVariableName)
        }, 300)
        return () => clearTimeout(timer)
    }

    populateGrid = (rowData) => {
        standardHandlePopulateGrid(rowData, this.gridApi)
    }

    updateGridForChangeStream = async (changeStreamData) => {
        const {operationType, body: obj} = changeStreamData
        if (operationType === "UPDATE" || operationType === "REPLACE") {
            // Preserve the zenGroupDisplayName already on the grid row since the
            // change stream body won't include it
            let rowNode = this.gridApi.getRowNode(obj.id)
            if (rowNode) {
                obj.zenGroupDisplayName = rowNode.data.zenGroupDisplayName
            }
            if (!this.gridApi.destroyCalled) {
                standardHandleUpdateAndReplaceEvent(obj, this.gridApi, this.props.sseDataPullActive, this.updateTransactionsToApply)
            }
        } else if (operationType === "INSERT") {
            standardHandleInsertEvent(obj, this.gridApi, this.props.sseDataPullActive)
        }
    }

    onGridReady = async (params) => {
        ["keyup", "keydown"].forEach(event => {
            window.addEventListener(event, (e) => {
                document.onselectstart = () => !(e.shiftKey || e.ctrlKey)
            })
        })

        this.gridApi = params.api
        this.props.setGridApi(params.api)

        const {
            columnMode, setColumnMode,
            gridColumnStateSessionVariableName,
            gridFilterStateSessionVariableName,
            minColumnIds, updateColumnModeReactive,
        } = this.props

        if (columnMode === customColumnModeText) {
            onGridReadyHelperForColumnState(params, gridColumnStateSessionVariableName)
        } else if (columnMode === minColumnModeText) {
            standardApplyMinimumOrMediumColumnMode(
                gridColumnStateSessionVariableName, params.api, setColumnMode,
                minColumnModeText, minColumnIds, updateColumnModeReactive
            )
        }

        loadDataWithSSEAndStartChangeStreamListener(
            "/sse/purgeHistoryListReactive",
            "/sse/listenToPurgeHistoryEvent",
            this.populateGrid,
            this.updateGridForChangeStream,
            params,
            this.props.setSSEDataPullActive,
            this.props.setAsyncTransactionWaitMillis,
            this.updateTransactionsToApply,
            this.abortController,
            null,
            this.applyFilterStateAfterSSE
        )
    }

    render() {
        return (
            <div className={"w-full h-full"} style={{minHeight: "400px"}}>
                <div id="purgeHistoryGrid" className="ag-theme-alpine rounded-md shadow h-full w-full">
                    <AgGridReact
                        columnMenu={"legacy"}
                        modules={[
                            AllCommunityModule,
                            ClientSideRowModelModule,
                            ColumnMenuModule,
                            ContextMenuModule,
                            ColumnsToolPanelModule,
                            SetFilterModule,
                            MultiFilterModule,
                            ExcelExportModule,
                            CsvExportModule,
                        ]}
                        defaultColDef={this.props.defaultColDef}
                        columnDefs={this.props.columnDefs}
                        components={{agDateInput: DTPicker, customNameCellEditor: CustomNameCellEditor}}
                        rowData={this.rowData}
                        asyncTransactionWaitMillis={this.props.asyncTransactionWaitMillis}
                        suppressModelUpdateAfterUpdateTransaction={true}
                        getRowId={(params) => params.data.id}
                        suppressCsvExport={true}
                        suppressMultiSort={true}
                        maintainColumnOrder={true}
                        onGridReady={this.onGridReady}
                        rowSelection={this.props.rowSelection}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        onFilterChanged={(params) => {
                            onFilterChangedHelper(
                                params,
                                this.props.gridFilterStateSessionVariableName,
                                this.props.updateFilterModelReactive
                            )
                        }}
                        onSortChanged={this.onColumnStateChanged}
                        onColumnMoved={this.onColumnStateChanged}
                        onColumnVisible={this.onColumnStateChanged}
                        getContextMenuItems={this.getContextMenuItems}
                        sideBar={this.props.sideBar}
                    />
                </div>
            </div>
        )
    }
}